import {
  ColumnApiModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ModuleRegistry,
  RenderApiModule,
  ValidationModule,
  type GridApi,
  type GridReadyEvent,
  type ColumnMovedEvent,
  type ColumnPinnedEvent,
  type ColumnResizedEvent,
  type ColumnVisibleEvent,
} from 'ag-grid-enterprise';
import { CustomerActionSummaryDto, Page } from '@ampsec/platform-client';
import { getGridData } from 'Apis/library';
import { EmptyState } from 'Components/molecules/EmptyState';
import EngageIcon from 'Assets/icons/engagement.svg';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  AgGridBase,
  useAgGridColumnStatePersistence,
  useClientExternalTextSearch,
  useFetchAllPages,
} from '../AgGridBase';
import {
  AgGridEngagementActionsProps,
  COLUMN_STATE_STORAGE_KEYS,
  CustomActionMenuValue,
  DEFAULT_COLUMN_STATE,
} from './AgGridEngagementActions.config';
import { getEngagementActionsColumns } from './getEngagementActionsColumns';
import './styles.scss';

ModuleRegistry.registerModules([
  ColumnApiModule,
  RenderApiModule,
  ValidationModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
]);

const AgGridEngagementActions: React.FC<AgGridEngagementActionsProps> = ({
  serviceProfiles,
  refreshToken,
  onEdit,
  onDelete,
  onExecution,
  onViewFailedRuns,
  tableBodyHeight = 'calc(100% - 3.625rem)',
}) => {
  const gridApiRef = useRef<GridApi<CustomerActionSummaryDto> | null>(null);

  const onEditRef = useRef(onEdit);
  const onDeleteRef = useRef(onDelete);
  const onExecutionRef = useRef(onExecution);
  const onViewFailedRunsRef = useRef(onViewFailedRuns);
  const onMenuActionRef = useRef((action: CustomActionMenuValue, row: CustomerActionSummaryDto) => {
    switch (action) {
      case 'DELETE':
        onDeleteRef.current(row);
        break;
      case 'EXECUTION':
        onExecutionRef.current(row);
        break;
      default:
        break;
    }
  });
  const onNameClickRef = useRef(onEdit);
  const onFailedRunsClickRef = useRef(onViewFailedRuns);

  onEditRef.current = onEdit;
  onDeleteRef.current = onDelete;
  onExecutionRef.current = onExecution;
  onViewFailedRunsRef.current = onViewFailedRuns;
  onNameClickRef.current = onEdit;
  onFailedRunsClickRef.current = onViewFailedRuns;

  const fetchPage = useCallback(
    (params: { isTemplate: boolean; offset: number; limit: number }) =>
      getGridData('customActions', params, 'summary') as Promise<Page<CustomerActionSummaryDto>>,
    [],
  );

  const baseParams = useMemo(() => ({ isTemplate: false }), []);

  const { rows, isLoading, refetch } = useFetchAllPages<
    CustomerActionSummaryDto,
    { isTemplate: boolean },
    Page<CustomerActionSummaryDto>
  >({
    fetchPage,
    baseParams,
    pageSize: 100,
  });

  const refreshTokenRef = useRef(refreshToken);
  useEffect(() => {
    if (refreshTokenRef.current === refreshToken) {
      return;
    }
    refreshTokenRef.current = refreshToken;
    refetch();
  }, [refreshToken, refetch]);

  const { handleSearchChange, isExternalFilterPresent, doesExternalFilterPass } =
    useClientExternalTextSearch<CustomerActionSummaryDto>({
      fields: [
        (row) => row.displayValue,
        (row) => row.serviceKey,
        (row) => String(row.recentActivity?.totalCount ?? ''),
        (row) => String(row.recentActivity?.failureCount ?? ''),
      ],
      onFilterChanged: () => gridApiRef.current?.onFilterChanged(),
    });

  const columnDefs = useMemo(
    () =>
      getEngagementActionsColumns({
        serviceProfiles,
        onNameClickRef,
        onFailedRunsClickRef,
        onMenuActionRef,
      }),
    [serviceProfiles],
  );

  const { applyOnGridReady, onColumnEvent } = useAgGridColumnStatePersistence({
    storageKey: COLUMN_STATE_STORAGE_KEYS.server,
    defaultState: DEFAULT_COLUMN_STATE,
  });

  const handleGridReady = useCallback(
    (event: GridReadyEvent<CustomerActionSummaryDto>) => {
      gridApiRef.current = event.api;
      applyOnGridReady(event.api as unknown as Parameters<typeof applyOnGridReady>[0]);
    },
    [applyOnGridReady],
  );

  const handleColumnPersistenceEvent = useCallback(
    (
      event:
        | ColumnResizedEvent<CustomerActionSummaryDto>
        | ColumnMovedEvent<CustomerActionSummaryDto>
        | ColumnVisibleEvent<CustomerActionSummaryDto>
        | ColumnPinnedEvent<CustomerActionSummaryDto>,
    ) => {
      onColumnEvent(event as unknown as Parameters<typeof onColumnEvent>[0]);
    },
    [onColumnEvent],
  );

  return (
    <div className="engagement-flow-actions-table ag-engagement-actions-grid h-100">
      <AgGridBase.Root>
        <AgGridBase.Header hidden />
        <AgGridBase.Table<CustomerActionSummaryDto>
          columnDefs={columnDefs}
          dataMode="client"
          rowData={rows}
          showSidebar={false}
          movableColumns={false}
          wrapperClassName="ag-engagement-actions-grid__wrapper ag-engagement-actions-grid__wrapper--theme-findings ag-grid-theme--amp-dark"
          bodyHeight={tableBodyHeight}
          showSearchBar
          onSearchChange={handleSearchChange}
          agGridProps={{
            loading: isLoading,
            getRowId: (params) => String(params.data?.id ?? ''),
            suppressAggFuncInHeader: true,
            suppressContextMenu: true,
            animateRows: false,
            isExternalFilterPresent,
            doesExternalFilterPass,
            onGridReady: handleGridReady,
            onGridPreDestroyed: () => {
              gridApiRef.current = null;
            },
            onColumnResized: handleColumnPersistenceEvent,
            onColumnMoved: handleColumnPersistenceEvent,
            onColumnVisible: handleColumnPersistenceEvent,
            onColumnPinned: handleColumnPersistenceEvent,
            noRowsOverlayComponent: EmptyState,
            noRowsOverlayComponentParams: {
              ImageSrc: EngageIcon,
              noDescription: false,
              description: 'No custom actions found',
            },
          }}
        />
      </AgGridBase.Root>
    </div>
  );
};

export default AgGridEngagementActions;
